import React, { useState, useMemo } from "react";
import gql from "graphql-tag";
import { fundraiserGroupParser, constants } from "@services";
import { Box, Text } from "@atoms";
import { RestrictedAccess } from "../components/RestrictedAccess";
import { HeaderLinks } from "../components/HeaderLinks";
import { MemberList } from "../components/MemberList";
import { MemberListGrouped } from "../components/MemberListGrouped";
import {
  OrganizeMembersViewFragment_fundraisers,
  OrganizeMembersViewFragment_groups,
} from "./__generated__/OrganizeMembersViewFragment";
// import styled from "@emotion/styled";

// == Types ================================================================

interface IProps {
  fundraisers: OrganizeMembersViewFragment_fundraisers[];
  groups: OrganizeMembersViewFragment_groups[];
  isViewerOwner: boolean;
  refetch: () => void;
}

// == Constants ============================================================

OrganizeMembersView.fragment = gql`
  fragment OrganizeMembersViewFragment on Campaign {
    id
    name
    privacyLevel
    isViewerOwner
    noGroupFundraisers {
      id
      name
      amountRaised {
        dollarsAmount
        formatted
      }
      paginatedFundraisers {
        edges {
          cursor
          node {
            id
            name
            firstName
            lastName
            email
            userId
            avatarImage
            isOwner
            joinedAt
            deactivatedAt
            group {
              id
              name
              amountRaised {
                dollarsAmount
                formatted
              }
            }
            amountRaised {
              formatted
              dollarsAmount
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    groups {
      id
      name
      amountRaised {
        dollarsAmount
        formatted
      }
      paginatedFundraisers {
        edges {
          cursor
          node {
            id
            name
            firstName
            lastName
            userId
            avatarImage
            isOwner
            joinedAt
            deactivatedAt
            group {
              id
              name
              amountRaised {
                dollarsAmount
                formatted
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    # fundraisers {
    #   id
    #   name
    #   firstName
    #   lastName
    #   userId
    #   avatarImage
    #   isOwner
    #   joinedAt
    #   deactivatedAt
    #   group {
    #     id
    #     name
    #     amountRaised {
    #       dollarsAmount
    #       formatted
    #     }
    #   }
    #   amountRaised {
    #     formatted
    #     dollarsAmount
    #   }
    # }
    ...HeaderLinksFragment
  }
  ${HeaderLinks.fragment}
`;

// == Functions ============================================================

const isGroupsChecker = (groupedFundraisers: OrganizeMembersViewFragment_groups[]) => {
  const groupsArray = Object.keys(groupedFundraisers);
  return (
    groupsArray.filter((group) => group !== "noGroup").filter((group) => group !== "pendingMembers")
      .length > 0
  );
};

// == Component ============================================================

export default function OrganizeMembersView({
  noGroupFundraisers,
  isViewerOwner,
  privacyLevel,
  groups,
  refetch,
}: IProps) {
  const sortedGroups = useMemo(
    () => groups?.sort((a, b) => b.amountRaised.dollarsAmount - a.amountRaised.dollarsAmount),
    [groups]
  );
  const groupedFundraisers = [...sortedGroups, noGroupFundraisers];
  const isGroups = isGroupsChecker(groupedFundraisers);
  const [activeReassignmentMemberId, setActiveReassignmentMemberId] = useState(null);
  const {
    PRIVACY_LEVEL_TYPES: { ANONYMOUS },
  } = constants;
  const isAnonymous = privacyLevel === ANONYMOUS;

  if (isAnonymous && !isViewerOwner) return <RestrictedAccess />;

  return (
    <Box>
      <Box padding={[3, 4]}>
        <Text color="black" fontSize={[3, 4]} fontWeight="bold">
          Organize Team Members
        </Text>
      </Box>
      <HeaderLinks groups={groups} />
      {isGroups ? (
        <MemberListGrouped
          activeReassignmentMemberId={activeReassignmentMemberId}
          groupedFundraisers={groupedFundraisers}
          groups={groups}
          isGroups={isGroups}
          isViewerOwner={isViewerOwner}
          refetch={refetch}
          setActiveReassignmentMemberId={setActiveReassignmentMemberId}
        />
      ) : (
        <MemberList
          activeReassignmentMemberId={activeReassignmentMemberId}
          groupedFundraisers={groupedFundraisers}
          isGroups={isGroups}
          isViewerOwner={isViewerOwner}
          refetch={refetch}
          setActiveReassignmentMemberId={setActiveReassignmentMemberId}
        />
      )}
    </Box>
  );
}

// == Styles ===============================================================
